import {
  ISearchFilterIndividualRequest,
  ISearchFilterOrganizationRequest,
} from 'models/searchFilter';
import { clearObject } from 'helpers/objects';
import { ISearchFormValues as ISearchLEIFormValues } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { ISearchFormValues as ISearchLEOFormValues } from 'pages/LegalEntitiesPage/OrganisationSearchForm';
import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { formatOnlyDateToRequest, simpleFormatDate } from 'utils/date';
import { parse } from 'date-fns';

const mapValuesToIndividualPresetRequest = (
  data: ISearchLEIFormValues,
  shortDateFormat: string,
): ISearchFilterIndividualRequest => {
  const values: ISearchFilterIndividualRequest = {
    firstname: data.firstname,
    lastName: data.lastName,
    city: data.city,
    birthdayFrom: data.birthdayFrom
      ? formatOnlyDateToRequest(
          parse(data.birthdayFrom as string, shortDateFormat, new Date()),
        )
      : '',
    birthdayTo: data.birthdayTo
      ? formatOnlyDateToRequest(parse(data.birthdayTo as string, shortDateFormat, new Date()))
      : '',
    visibilityUniqueId: data.visibilityUniqueId,
    gendersUniqueIds: data.gendersUniqueIds,
    prefixGroupsUniqueIds: data.prefixGroupsUniqueIds,
    displayUniqueId: data.displayUniqueId,
    identityDocumentNumber: data.identityDocumentNumber,
    statusUniqueId: data.statusUniqueId,
  };

  return clearObject(values);
};

const mapValuesToOrganisationPresetRequest = (
  data: ISearchLEOFormValues,
): ISearchFilterOrganizationRequest => {
  const values: ISearchFilterOrganizationRequest = {
    name: data.name,
    organizationTypesUniqueIds: data.organizationTypesUniqueIds,
    departmentName: data.departmentName,
    city: data.city,
    visibilityUniqueId: data.visibilityUniqueId,
    displayUniqueId: data.displayUniqueId,
    organisationCategoriesUniqueIds: data.organisationCategoriesUniqueIds,
    statusUniqueId: data.statusUniqueId,
  };
  return clearObject(values);
};

export const mapValuesToPresetRequest = (
  type: ESearchFilterKeys | ELegalEntityTypes | undefined,
  data: ISearchLEIFormValues | ISearchLEOFormValues,
  shortDateFormat: string,
) => {
  if (!type) {
    return null;
  }

  switch (type) {
    case ESearchFilterKeys.individual:
    case ELegalEntityTypes.individual:
      return mapValuesToIndividualPresetRequest(data as ISearchLEIFormValues, shortDateFormat);

    case ESearchFilterKeys.organisation:
    case ELegalEntityTypes.organisation:
      return mapValuesToOrganisationPresetRequest(data as ISearchLEOFormValues);
    default:
      return null;
  }
};

export const permittedOrganisationFilterFields = [
  'name',
  'organizationTypesUniqueIds',
  'departmentName',
  'city',
  'visibilityUniqueId',
  'displayUniqueId',
  'organisationCategoriesUniqueIds',
  'statusUniqueId',
];

export const permittedIndividualFilterFields = [
  'firstname',
  'lastName',
  'city',
  'birthdayFrom',
  'birthdayTo',
  'visibilityUniqueId',
  'gendersUniqueIds',
  'prefixGroupsUniqueIds',
  'displayUniqueId',
  'identityDocumentNumber',
  'statusUniqueId',
];

export const mapValuesToIndividualPresetForView = (
  values: { key: string; value: string }[],
  shortDateFormat: string,
) => {
  values.unshift({ key: 'legalEntityType', value: 'Individual' });
  return values.map((item) => {
    switch (item.key) {
      case 'birthdayFrom':
        return {
          key: 'birthdayFrom',
          value: simpleFormatDate(new Date(item.value), shortDateFormat),
        };
      case 'birthdayTo':
        return {
          key: 'birthdayTo',
          value: simpleFormatDate(new Date(item.value), shortDateFormat),
        };
      default:
        return item;
    }
  });
};

export const mapValuesToOrganisationPresetForView = (
  values: { key: string; value: string }[],
) => {
  values.unshift({ key: 'legalEntityType', value: 'Organisation' });
  return values;
};
